// ── FORMATTING ─────────────────────────────────────────────────────────────
export const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

export const fmtDec = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export const fmtPct = (n) =>
  `${(n || 0).toFixed(1)}%`;

// ── DATE HELPERS ───────────────────────────────────────────────────────────
export const today = () => new Date().toISOString().split('T')[0];

export const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

export function weekKey(dateStr) {
  const d   = new Date(dateStr + 'T12:00:00');
  const jan1 = new Date(d.getFullYear(), 0, 1);
  const week = Math.ceil(((d - jan1) / 86400000 + jan1.getDay() + 1) / 7);
  return `${d.getFullYear()}-W${String(week).padStart(2, '0')}`;
}
export const monthKey   = (d) => d.slice(0, 7);
export const quarterKey = (d) => {
  const [y, m] = d.split('-');
  return `${y}-Q${Math.ceil(parseInt(m) / 3)}`;
};
export const yearKey = (d) => d.slice(0, 4);

export function getLast30Days(transactions) {
  const days = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split('T')[0];
    const dayTx   = transactions.filter((t) => t.date === dateStr);
    days.push({
      date:      dateStr,
      label:     dateStr.slice(5),
      sales:     dayTx.filter((t) => t.direction === 'Sale').reduce((s, t) => s + (t.totalAmount || 0), 0),
      purchases: dayTx.filter((t) => t.direction === 'Purchase').reduce((s, t) => s + (t.totalAmount || 0), 0),
    });
  }
  return days;
}

// ── STORAGE ────────────────────────────────────────────────────────────────
export const LS_KEYS = {
  transactions: 'agc:transactions',
  invoices:     'agc:invoices',
  settings:     'agc:settings',
  lastBackup:   'agc:lastBackup',
};

export function loadLS(key, fallback = null) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; }
  catch { return fallback; }
}
export function saveLS(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

// ── AGGREGATION ────────────────────────────────────────────────────────────
export function sumField(arr, direction, channel, field = 'totalAmount') {
  return arr
    .filter((t) => (!direction || t.direction === direction) && (!channel || t.channel === channel))
    .reduce((s, t) => s + (t[field] || 0), 0);
}

export function buildKPIRow(txs, label) {
  const retailSales  = sumField(txs, 'Sale', 'Retail');
  const wholeSales   = sumField(txs, 'Sale', 'Wholesale');
  const retailBuys   = sumField(txs, 'Purchase', 'Retail');
  const wholeBuys    = sumField(txs, 'Purchase', 'Wholesale');
  const totalSales   = retailSales + wholeSales;
  const totalBuys    = retailBuys + wholeBuys;
  const margin       = totalSales - totalBuys;
  const marginPct    = totalSales > 0 ? (margin / totalSales) * 100 : 0;
  return { label, txCount: txs.length, retailSales, wholeSales, totalSales, retailBuys, wholeBuys, totalBuys, margin, marginPct };
}

// ── METALS PRICE FETCH ─────────────────────────────────────────────────────
export async function fetchMetalPrices() {
  try {
    const res  = await fetch('https://api.metals.live/v1/spot/gold,silver,platinum,palladium');
    if (!res.ok) throw new Error('bad response');
    const data = await res.json();
    const map  = {};
    data.forEach((item) => { map[item.metal] = item.price; });
    return {
      Gold:      map.gold      || null,
      Silver:    map.silver    || null,
      Platinum:  map.platinum  || null,
      Palladium: map.palladium || null,
      timestamp: new Date().toLocaleTimeString(),
      ok:        true,
    };
  } catch {
    return { Gold: null, Silver: null, Platinum: null, Palladium: null, timestamp: 'unavailable', ok: false };
  }
}

// ── GOOGLE DRIVE BACKUP ────────────────────────────────────────────────────
export function buildBackupBlob(transactions, invoices, settings) {
  const payload = {
    app:         'Atlanta Gold & Coin Buyers — Operations Dashboard',
    exportedAt:  new Date().toISOString(),
    version:     '1.0',
    settings,
    summary: {
      totalTransactions: transactions.length,
      totalInvoices:     invoices.length,
    },
    transactions,
    invoices,
  };
  return new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
}

export function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href    = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportCSV(transactions) {
  const headers = ['Date','Direction','Channel','Category','Description','Metal','Qty','UnitPrice','TotalAmount','WeightOz','Vendor','InvoiceRef','Notes'];
  const rows = transactions.map((t) => [
    t.date, t.direction, t.channel, t.category,
    `"${(t.description || '').replace(/"/g, '""')}"`,
    t.metal, t.qty, t.unitPrice, t.totalAmount, t.weight || '',
    `"${(t.vendor || '').replace(/"/g, '""')}"`,
    t.invoiceNumber || '', `"${(t.notes || '').replace(/"/g, '""')}"`,
  ]);
  const csv  = [headers, ...rows].map((r) => r.join(',')).join('\n');
  return new Blob([csv], { type: 'text/csv' });
}
